import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@nx/ui';
import { useI18n } from '@nx/i18n';
import { isAuthError } from '@nx/shared';
import { useAppDispatch } from '../store/hooks';
import { authSucceeded } from '../store/slices/authSlice';
import { authRepository } from '../services/firebase';

type Status = 'verifying' | 'success' | 'expired' | 'invalid' | 'error' | 'unsupported';

function getErrorStatus(error: unknown): Status {
  if (isAuthError(error)) {
    if (error.code === 'auth/expired-action-code') return 'expired';
    if (error.code === 'auth/invalid-action-code') return 'invalid';
  }
  return 'error';
}

export function AuthAction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useI18n();

  const [status, setStatus] = useState<Status>('verifying');

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    if (mode !== 'verifyEmail' || !oobCode) {
      setStatus(mode ? 'unsupported' : 'invalid');
      return;
    }

    let cancelled = false;

    authRepository
      .applyVerificationCode(oobCode)
      .then(async () => {
        // Reload the Firebase user so emailVerified flips to true in memory.
        await authRepository.reloadCurrentUser().catch(() => {
          // User may not be signed in on this device — that's fine.
        });

        const fresh = await authRepository.getCurrentIdentity();
        if (!cancelled) {
          if (fresh) {
            dispatch(authSucceeded({ identity: fresh, message: null }));
          }
          setStatus('success');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) setStatus(getErrorStatus(error));
      });

    return () => {
      cancelled = true;
    };
    // Run once on mount — searchParams is stable for this URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to dashboard a moment after showing the success message.
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 2000);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-[#3D3328]/10 rounded-2xl shadow-lg p-8 text-center">
        {status === 'verifying' && <VerifyingState t={t} />}
        {status === 'success' && <SuccessState t={t} />}
        {status !== 'verifying' && status !== 'success' && (
          <ErrorState status={status} t={t} navigate={navigate} />
        )}
      </div>
    </div>
  );
}

function VerifyingState({ t }: { t: (key: Parameters<ReturnType<typeof useI18n>['t']>[0]) => string }) {
  return (
    <div className="flex flex-col items-center gap-4 text-[#78665A]">
      <Loader2 className="h-12 w-12 animate-spin text-[#3D3328]" />
      <p className="text-base font-medium text-[#3D3328]">{t('authAction.verifying')}</p>
    </div>
  );
}

function SuccessState({ t }: { t: (key: Parameters<ReturnType<typeof useI18n>['t']>[0]) => string }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="rounded-full bg-green-100 p-4">
        <CheckCircle2 className="h-10 w-10 text-green-600" />
      </div>
      <h1 className="text-2xl font-bold text-[#3D3328]">{t('authAction.successTitle')}</h1>
      <p className="text-sm text-[#78665A]">{t('authAction.successDesc')}</p>
      <Loader2 className="h-5 w-5 animate-spin text-[#78665A]" />
    </div>
  );
}

function ErrorState({
  status,
  t,
  navigate,
}: {
  status: Exclude<Status, 'verifying' | 'success'>;
  t: (key: Parameters<ReturnType<typeof useI18n>['t']>[0]) => string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const titleKey = `authAction.${status}Title` as const;
  const descKey = `authAction.${status}Desc` as const;
  const showResend = status === 'expired' || status === 'invalid';

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="rounded-full bg-red-100 p-4">
        <XCircle className="h-10 w-10 text-red-500" />
      </div>
      <h1 className="text-2xl font-bold text-[#3D3328]">{t(titleKey)}</h1>
      <p className="text-sm text-[#78665A]">{t(descKey)}</p>
      <div className="flex flex-col gap-2 w-full mt-2">
        {showResend && (
          <Button onClick={() => navigate('/verify-email', { replace: true })}>
            {t('authAction.resendLink')}
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
          {t('authAction.goToLogin')}
        </Button>
      </div>
    </div>
  );
}
